/**
 * Los datos legales de la empresa, leídos del entorno en build time (FEATURE-178 RF-04).
 *
 * **Lo que falta sale en blanco.** No hay placeholder, ni marcador, ni valor inventado: si
 * la variable no está definida, el dato no se renderiza y —esto es lo importante— tampoco
 * el texto que lo rodea, para que no queden comas huérfanas («operado por , RUT , con
 * domicilio en , Chile»). De eso se encarga quien renderiza el contenido, que omite la
 * oración completa cuando el dato que necesita no existe (RF-04.1, CB-004).
 *
 * Son `NEXT_PUBLIC_*` porque con static export no hay servidor: el valor se inlinea en el
 * HTML al compilar. Ninguno es secreto —son datos que van impresos en un documento
 * público— así que exponerlos al navegador es exactamente lo que corresponde.
 *
 * **Riesgo conocido (R-005):** con todo en blanco el documento se lee perfecto y por eso
 * nadie nota que le falta la razón social. `datosLegalesFaltantes` existe para que ese
 * aviso pueda vivir donde alguien lo vea.
 */

/** Un valor de entorno vacío o solo con espacios cuenta como ausente. */
const leer = (valor: string | undefined): string | null => {
  const limpio = valor?.trim();
  return limpio ? limpio : null;
};

export const LEGAL = {
  /** Razón social de la empresa que opera el servicio. */
  entityName: leer(process.env.NEXT_PUBLIC_LEGAL_ENTITY_NAME),
  /** RUT de la empresa. */
  entityTaxId: leer(process.env.NEXT_PUBLIC_LEGAL_ENTITY_TAX_ID),
  /** Domicilio legal. */
  entityAddress: leer(process.env.NEXT_PUBLIC_LEGAL_ENTITY_ADDRESS),
  /** Comuna o jurisdicción de los tribunales competentes. */
  jurisdiction: leer(process.env.NEXT_PUBLIC_LEGAL_JURISDICTION),
  /** Fecha de publicación del documento vigente. */
  effectiveDate: leer(process.env.NEXT_PUBLIC_LEGAL_EFFECTIVE_DATE),
  /** Aviso previo al terminar el servicio. */
  terminationNotice: leer(process.env.NEXT_PUBLIC_LEGAL_TERMINATION_NOTICE),
  /** Límite de responsabilidad. */
  liabilityCap: leer(process.env.NEXT_PUBLIC_LEGAL_LIABILITY_CAP),
  /** Plazo de eliminación de datos tras cerrar la cuenta. */
  deletionPeriod: leer(process.env.NEXT_PUBLIC_LEGAL_DELETION_PERIOD),
  /** Plazo de respuesta a un ejercicio de derechos. */
  responsePeriod: leer(process.env.NEXT_PUBLIC_LEGAL_RESPONSE_PERIOD),
  /** Región donde se alojan los datos. */
  dataRegion: leer(process.env.NEXT_PUBLIC_LEGAL_DATA_REGION),
  /** Proveedor de correo saliente, que aparece en la lista de terceros. */
  emailProvider: leer(process.env.NEXT_PUBLIC_LEGAL_EMAIL_PROVIDER),
} as const;

export type DatoLegal = keyof typeof LEGAL;

/** Los canales de contacto. No son de entorno: existen y son estables. */
export const CORREO_PRIVACIDAD = "privacidad@latablapp.cl";

/** Qué datos legales siguen sin definirse. Para avisar en el despliegue (R-005). */
export const datosLegalesFaltantes = (): DatoLegal[] =>
  (Object.keys(LEGAL) as DatoLegal[]).filter((k) => LEGAL[k] === null);
